import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import type { MagicWorkflow } from '@/features/experiences/components/CustomizeMode/customizeOptions';
import type { DraftEventDefaults } from '../../lib/draftEventFactory';

export type LaunchState = 'onboarding' | 'default';

type OpenOptions = {
  initialFile?: File;
  initialModel?: string;
  initialSystemPromptId?: string;
  initialText?: string;
  workflow?: MagicWorkflow;
};

type State = {
  isOpen: boolean;
  initialFile: File | null;
  initialModel: string | null;
  initialSystemPromptId: string | null;
  initialText: string | null;
  workflow: MagicWorkflow | null;
  /** Opened by the planner guide, whose card stays usable beside the modal. */
  isGuided: boolean;
  isGuideHolding: boolean;
  setGuideHold: (isGuideHolding: boolean) => void;
  open: (options?: OpenOptions) => void;
  openGuided: () => void;
  close: () => void;
  /**
   * Clear the open request without dropping the payload. Used by the panel's
   * store→URL bridge: once `open()` has been translated into the `edit=magic`
   * param (the panel's source of truth), the store flag is consumed so the URL
   * alone drives presence (back button / refresh behave correctly).
   */
  consumeOpen: () => void;
  clearInitialFile: () => void;
  /** Text only: `clearInitialPayload` would drop the file before it's attached. */
  clearInitialText: () => void;
  clearInitialModelAndPrompt: () => void;
  /**
   * Drop the whole payload without touching the open request, for one that no
   * composer ever consumed. `consumeOpen` deliberately keeps the payload so a
   * chat can still read it once the URL owns presence.
   */
  clearInitialPayload: () => void;
  /** Manual event entry, e.g. from a planner day click. */
  isCreateEventOpen: boolean;
  /** Bumped per open so the modal remounts on a clean draft. */
  createEventKey: number;
  /** What the draft starts from, e.g. the planner day that was clicked. */
  createEventDefaults: DraftEventDefaults | null;
  openCreateEvent: (defaults?: DraftEventDefaults) => void;
  closeCreateEvent: () => void;
};

export const useAddContentModal = createWithEqualityFn<State>()(
  (set, get) => ({
    isOpen: false,
    initialFile: null,
    initialModel: null,
    initialSystemPromptId: null,
    initialText: null,
    workflow: null,
    isGuided: false,
    isGuideHolding: false,
    setGuideHold: (isGuideHolding) =>
      set(({ isOpen }) => ({
        isGuideHolding,
        isGuided: isOpen && isGuideHolding,
      })),
    open: ({
      initialFile,
      initialModel,
      initialSystemPromptId,
      initialText,
      workflow,
    }: OpenOptions = {}) =>
      set({
        isOpen: true,
        initialFile: initialFile ?? null,
        initialModel: initialModel ?? null,
        initialSystemPromptId: initialSystemPromptId ?? null,
        initialText: initialText ?? null,
        workflow: workflow ?? null,
        isGuided: get().isGuideHolding,
      }),
    openGuided: () =>
      set({
        isOpen: true,
        initialFile: null,
        initialModel: null,
        initialSystemPromptId: null,
        initialText: null,
        workflow: null,
        isGuided: true,
      }),
    close: () =>
      set({
        isOpen: false,
        initialFile: null,
        initialModel: null,
        initialSystemPromptId: null,
        initialText: null,
        workflow: null,
        isGuided: false,
      }),
    consumeOpen: () => set({ isOpen: false, workflow: null }),
    clearInitialFile: () => set({ initialFile: null }),
    clearInitialText: () => set({ initialText: null }),
    clearInitialModelAndPrompt: () =>
      set({ initialModel: null, initialSystemPromptId: null }),
    clearInitialPayload: () =>
      set({
        initialFile: null,
        initialModel: null,
        initialSystemPromptId: null,
        initialText: null,
      }),
    isCreateEventOpen: false,
    createEventKey: 0,
    createEventDefaults: null,
    openCreateEvent: (defaults) =>
      set((state) => ({
        isCreateEventOpen: true,
        createEventKey: state.createEventKey + 1,
        createEventDefaults: defaults ?? null,
      })),
    closeCreateEvent: () =>
      set({ isCreateEventOpen: false, createEventDefaults: null }),
  }),
  shallow
);
